import { Box, Container, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

function AnimatedCounter({ value, suffix = '', duration = 2 }: { value: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, (duration * 1000) / steps);
    return () => clearInterval(timer);
  }, [isVisible, value, duration]);

  return (
    <Box ref={ref} sx={{ textAlign: 'center' }}>
      <Typography
        sx={{
          fontSize: { xs: 36, md: 56 },
          fontWeight: 800,
          letterSpacing: '-0.02em',
          background: 'linear-gradient(135deg, #f0f0f5, #a0a5b5)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          lineHeight: 1,
        }}
      >
        {count.toLocaleString()}{suffix}
      </Typography>
    </Box>
  );
}

export function SocialProofBar() {
  const stats = [
    { value: 1200, suffix: '+', label: 'Companies' },
    { value: 50000, suffix: '+', label: 'Employees Managed' },
    { value: 99, suffix: '.9%', label: 'Uptime' },
    { value: 100, suffix: '%', label: 'SA Compliant' },
  ];

  return (
    <Box sx={{ py: 8, bgcolor: '#0a0a18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <Container maxWidth="lg">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 4, md: 8 }} justifyContent="center" sx={{ mb: 6 }}>
          {stats.map((stat) => (
            <Stack key={stat.label} spacing={0.5} alignItems="center">
              <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              <Typography sx={{ color: '#a0a5b5', fontSize: 13, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {stat.label}
              </Typography>
            </Stack>
          ))}
        </Stack>

        {/* Logo marquee placeholder */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, opacity: 0.3, flexWrap: 'wrap' }}>
          {['TechCorp', 'ManufacturingCo', 'RetailSA', 'FinServ', 'HealthPlus', 'EduLearn'].map((name) => (
            <Typography key={name} sx={{ fontSize: 18, fontWeight: 700, color: '#a0a5b5', letterSpacing: '0.05em' }}>
              {name}
            </Typography>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
